import React, { useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useAuth } from '../../auth/AuthContext';
import {
  ApiError,
  CheckoutEvent,
  getOrder,
  getPaymentAttempts,
  listOrders,
  Order,
  PaymentAttempt,
  PaymentAttempts,
  OrdersPage as OrdersPageData,
  OrderWithLines,
  setOrderStatus,
} from '../../api';

const fmtMoney = (s: string) => `$${s}`;
const fmtDate = (s: string) => new Date(s).toLocaleString();
const shortId = (id: string) => id.slice(0, 8);

const TERMINAL_STATUSES = new Set(['fulfilled', 'cancelled', 'refunded']);
const PRE_2025_CUTOFF = '2025-01-01T00:00:00Z';

// Order state machine. Ported from tqos-admin's `ReviewState` widget with
// the vocab re-keyed onto the new Postgres lowercase statuses (NEW/OPEN
// dropped — the new schema starts at `pending`; DELIVERED → `fulfilled`;
// CLOSED dropped — no equivalent in the new schema). Terminal statuses
// map to an empty list so the StatusControl renders as plain text.
const STATUS_TRANSITIONS: Record<string, string[]> = {
  pending: ['paid', 'cancelled'],
  paid: ['shipped', 'refunded'],
  shipped: ['fulfilled', 'refunded'],
  fulfilled: ['refunded'],
  cancelled: [],
  refunded: [],
};

const allowedTransitions = (status: string): string[] =>
  STATUS_TRANSITIONS[status] ?? [];

// Display-only derivation, mirroring tqos-server `fixState()`. The
// underlying status stays `pending`; this is purely a label.
const displayStatus = (order: Order): string => {
  if (order.status === 'pending' && order.payment_method === 'card') {
    return 'card incomplete';
  }
  return order.status;
};

const isAttentionStatus = (order: Order): boolean =>
  order.status === 'pending' && order.payment_method === 'card';

const statusSx = (order: Order) => {
  if (isAttentionStatus(order)) return { color: '#b45309', fontWeight: 500 }; // amber
  if (order.status === 'paid') return { color: '#15803d', fontWeight: 500 }; // green
  return undefined;
};

// Filter state — single source of truth for the dialog and the query.
type Filters = {
  email: string | null;
  includeTest: boolean;
  includeCancelled: boolean;
  hidePre2025: boolean;
};

const DEFAULT_FILTERS: Filters = {
  email: null,
  includeTest: false,
  includeCancelled: false,
  hidePre2025: true,
};

const activeFilterCount = (f: Filters): number => {
  let n = 0;
  if (f.email) n++;
  if (f.includeTest !== DEFAULT_FILTERS.includeTest) n++;
  if (f.includeCancelled !== DEFAULT_FILTERS.includeCancelled) n++;
  if (f.hidePre2025 !== DEFAULT_FILTERS.hidePre2025) n++;
  return n;
};

const OrdersPage = () => {
  const { idToken } = useAuth();

  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const [data, setData] = useState<OrdersPageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  const [detail, setDetail] = useState<OrderWithLines | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string>('');

  useEffect(() => {
    if (!idToken) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    const since = filters.hidePre2025 ? PRE_2025_CUTOFF : null;
    listOrders(
      idToken,
      filters.email,
      filters.includeTest,
      filters.includeCancelled,
      since,
      rowsPerPage,
      page * rowsPerPage,
    )
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          err instanceof ApiError
            ? `API error ${err.status}`
            : (err as Error)?.message || 'fetch failed',
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [idToken, filters, page, rowsPerPage]);

  // Any filter change resets to page 0.
  const updateFilters = (next: Filters) => {
    setFilters(next);
    setPage(0);
  };

  const openDetail = async (id: string) => {
    if (!idToken) return;
    setDetailLoading(true);
    setDetailError('');
    setDetail(null);
    try {
      const o = await getOrder(idToken, id);
      setDetail(o);
    } catch (err) {
      setDetailError(
        err instanceof ApiError
          ? `API error ${err.status}`
          : (err as Error)?.message || 'fetch failed',
      );
    } finally {
      setDetailLoading(false);
    }
  };

  // Status transitions echo back the updated row; patch it into both the
  // dialog and the list (in-place) so the UI doesn't need a full reload.
  const handleStatusChanged = (updated: Order) => {
    setDetail((prev) => (prev ? { ...prev, ...updated } : prev));
    setData((prev) =>
      prev
        ? {
            ...prev,
            items: prev.items.map((o) => (o.id === updated.id ? updated : o)),
          }
        : prev,
    );
  };

  const activeCount = activeFilterCount(filters);

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, color: '#1f2937' }}>
        Orders
      </Typography>
      <Typography variant="body2" sx={{ color: '#64748b', mb: 3 }}>
        Most recent first.
      </Typography>

      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Badge
          badgeContent={activeCount}
          color="primary"
          invisible={activeCount === 0}
        >
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={() => setFilterDialogOpen(true)}
            sx={{ textTransform: 'none' }}
          >
            Filter
          </Button>
        </Badge>
        <FilterSummary filters={filters} />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{ border: '1px solid #e5e7eb', borderRadius: 1 }}
      >
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#fafafa' }}>
              <TableCell sx={{ fontWeight: 600 }}>Order</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Customer</TableCell>
              <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Total</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && data === null ? (
              <LoadingRow />
            ) : (data?.items.length ?? 0) === 0 ? (
              <EmptyRow text="No orders match the current filters." />
            ) : (
              data!.items.map((o) => (
                <OrderRow key={o.id} order={o} onOpen={() => openDetail(o.id)} />
              ))
            )}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={data?.total ?? 0}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[25, 50, 100, 200]}
        />
      </TableContainer>

      <FilterDialog
        open={filterDialogOpen}
        filters={filters}
        onChange={updateFilters}
        onClose={() => setFilterDialogOpen(false)}
      />

      <Dialog
        open={detail !== null || detailLoading || !!detailError}
        onClose={() => {
          setDetail(null);
          setDetailError('');
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box sx={{ fontFamily: 'monospace', fontSize: '0.95rem' }}>
            {detail?.id || (detailLoading ? 'Loading…' : 'Order')}
          </Box>
          <IconButton
            size="small"
            onClick={() => {
              setDetail(null);
              setDetailError('');
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          )}
          {detailError && <Alert severity="error">{detailError}</Alert>}
          {detail && (
            <OrderDetail detail={detail} onStatusChanged={handleStatusChanged} />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

const FilterSummary = ({ filters }: { filters: Filters }) => {
  const parts: string[] = [];
  if (filters.email) parts.push(`email = ${filters.email}`);
  if (filters.includeTest) parts.push('including test');
  if (filters.includeCancelled) parts.push('including cancelled');
  if (!filters.hidePre2025) parts.push('including pre-2025');
  if (parts.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: '#94a3b8', fontStyle: 'italic' }}>
        No filters (default view: 2025+, no test, no cancelled)
      </Typography>
    );
  }
  return (
    <Typography variant="body2" sx={{ color: '#64748b' }}>
      {parts.join(' · ')}
    </Typography>
  );
};

const FilterDialog = ({
  open,
  filters,
  onChange,
  onClose,
}: {
  open: boolean;
  filters: Filters;
  onChange: (next: Filters) => void;
  onClose: () => void;
}) => {
  const [emailDraft, setEmailDraft] = useState(filters.email ?? '');

  // Reset the input when the dialog opens (so prior typing doesn't linger).
  useEffect(() => {
    if (open) setEmailDraft(filters.email ?? '');
  }, [open, filters.email]);

  const applyEmail = () => {
    const trimmed = emailDraft.trim();
    onChange({ ...filters, email: trimmed === '' ? null : trimmed });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        Filters
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          <Box>
            <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.5 }}>
              Customer email
            </Typography>
            <Box
              component="form"
              onSubmit={(e) => {
                e.preventDefault();
                applyEmail();
              }}
              sx={{ display: 'flex', gap: 1 }}
            >
              <TextField
                type="email"
                value={emailDraft}
                onChange={(e) => setEmailDraft(e.target.value)}
                size="small"
                placeholder="customer@example.com"
                fullWidth
              />
              <Button
                type="submit"
                variant="contained"
                size="small"
                sx={{ textTransform: 'none' }}
              >
                Apply
              </Button>
            </Box>
          </Box>

          <FormControlLabel
            control={
              <Switch
                checked={filters.hidePre2025}
                onChange={(e) =>
                  onChange({ ...filters, hidePre2025: e.target.checked })
                }
              />
            }
            label="Hide orders from before 2025"
          />

          <FormControlLabel
            control={
              <Switch
                checked={filters.includeCancelled}
                onChange={(e) =>
                  onChange({ ...filters, includeCancelled: e.target.checked })
                }
              />
            }
            label="Show cancelled orders"
          />

          <FormControlLabel
            control={
              <Switch
                checked={filters.includeTest}
                onChange={(e) =>
                  onChange({ ...filters, includeTest: e.target.checked })
                }
              />
            }
            label="Show test orders"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => onChange(DEFAULT_FILTERS)}
          sx={{ textTransform: 'none' }}
        >
          Reset to defaults
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={onClose}
          variant="contained"
          sx={{ textTransform: 'none' }}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const LoadingRow = () => (
  <TableRow>
    <TableCell colSpan={6} sx={{ textAlign: 'center', py: 6, borderBottom: 'none' }}>
      <CircularProgress size={20} />
    </TableCell>
  </TableRow>
);

const EmptyRow = ({ text }: { text: string }) => (
  <TableRow>
    <TableCell
      colSpan={6}
      sx={{
        textAlign: 'center',
        color: '#94a3b8',
        py: 6,
        fontStyle: 'italic',
        borderBottom: 'none',
      }}
    >
      {text}
    </TableCell>
  </TableRow>
);

const OrderRow = ({ order, onOpen }: { order: Order; onOpen: () => void }) => {
  const terminal = TERMINAL_STATUSES.has(order.status);
  const displayId = order.legacy_business_id ?? shortId(order.id);
  return (
    <TableRow
      hover
      sx={{
        opacity: terminal ? 0.65 : 1,
        '& .MuiTableCell-root': order.is_test
          ? { color: '#94a3b8', fontStyle: 'italic' }
          : undefined,
      }}
    >
      <TableCell sx={{ fontFamily: 'monospace' }}>{displayId}</TableCell>
      <TableCell>{fmtDate(order.created_at)}</TableCell>
      <TableCell>{order.customer_email}</TableCell>
      <TableCell sx={{ textAlign: 'right' }}>{fmtMoney(order.total)}</TableCell>
      <TableCell sx={statusSx(order)}>{displayStatus(order)}</TableCell>
      <TableCell sx={{ textAlign: 'right' }}>
        <Button
          size="small"
          variant="text"
          onClick={onOpen}
          sx={{ textTransform: 'none' }}
        >
          View
        </Button>
      </TableCell>
    </TableRow>
  );
};

const displayShipping = (method: string | null): string => {
  if (!method) return '—';
  if (method === 'PICKUP') return 'Pick-up';
  if (method === 'LOCAL') return 'Local delivery';
  return 'Standard shipping';
};

const formatAddress = (addr: unknown): string[] => {
  if (!addr || typeof addr !== 'object') return [];
  const a = addr as Record<string, any>;
  const lines: string[] = [];
  const name = [a.firstName, a.lastName].filter(Boolean).join(' ').trim();
  if (name) lines.push(name);
  const street = [a.street, a.additional].filter(Boolean).join(', ').trim();
  if (street) lines.push(street);
  const cityLine = [
    a.city,
    [a.province, a.postal].filter(Boolean).join(' ').trim(),
  ]
    .filter(Boolean)
    .join(', ')
    .trim();
  if (cityLine) lines.push(cityLine);
  if (a.country) lines.push(String(a.country));
  return lines;
};

const AddressBlock = ({ label, addr }: { label: string; addr: unknown }) => {
  const lines = formatAddress(addr);
  if (lines.length === 0) return null;
  return (
    <Box>
      <Typography
        variant="caption"
        sx={{ color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', mb: 0.5 }}
      >
        {label}
      </Typography>
      <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
        {lines.map((line, i) => (
          <React.Fragment key={i}>
            {line}
            {i < lines.length - 1 && <br />}
          </React.Fragment>
        ))}
      </Typography>
    </Box>
  );
};

const StatusControl = ({
  order,
  onChanged,
}: {
  order: Order;
  onChanged: (updated: Order) => void;
}) => {
  const { idToken } = useAuth();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string>('');

  const options = allowedTransitions(order.status);

  // Terminal (or otherwise no-transitions) — render as plain text so the
  // detail row still shows the status, matching the legacy admin's
  // behavior of disabling the dropdown when there's nowhere to go.
  if (options.length === 0) {
    return <Field label="Status" value={displayStatus(order)} />;
  }

  const handleChange = async (e: SelectChangeEvent<string>) => {
    const next = e.target.value;
    if (!idToken || next === order.status) return;
    setBusy(true);
    setErr('');
    try {
      const updated = await setOrderStatus(idToken, order.id, next);
      onChanged(updated);
    } catch (e) {
      setErr(
        e instanceof ApiError
          ? `API error ${e.status}`
          : (e as Error)?.message || 'status update failed',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      <Typography
        variant="caption"
        sx={{
          color: '#94a3b8',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          display: 'block',
        }}
      >
        Status
      </Typography>
      <Select
        size="small"
        value={order.status}
        onChange={handleChange}
        disabled={busy}
        sx={{ minWidth: 140, mt: 0.25, fontSize: '0.85rem' }}
      >
        <MenuItem value={order.status} disabled>
          {displayStatus(order)}
        </MenuItem>
        {options.map((s) => (
          <MenuItem key={s} value={s}>
            {s}
          </MenuItem>
        ))}
      </Select>
      {err && (
        <Typography
          variant="caption"
          color="error"
          sx={{ display: 'block', mt: 0.5 }}
        >
          {err}
        </Typography>
      )}
    </Box>
  );
};

const OrderDetail = ({
  detail,
  onStatusChanged,
}: {
  detail: OrderWithLines;
  onStatusChanged: (updated: Order) => void;
}) => (
  <Stack spacing={3}>
    <Stack direction="row" spacing={4} flexWrap="wrap">
      <Field label="Customer" value={detail.customer_email} />
      <StatusControl order={detail} onChanged={onStatusChanged} />
      <Field label="Created" value={fmtDate(detail.created_at)} />
      {detail.paid_at && <Field label="Paid" value={fmtDate(detail.paid_at)} />}
      {detail.shipped_at && <Field label="Shipped" value={fmtDate(detail.shipped_at)} />}
      {detail.fulfilled_at && <Field label="Fulfilled" value={fmtDate(detail.fulfilled_at)} />}
      {detail.cancelled_at && <Field label="Cancelled" value={fmtDate(detail.cancelled_at)} />}
      {detail.refunded_at && <Field label="Refunded" value={fmtDate(detail.refunded_at)} />}
      {detail.payment_method && (
        <Field label="Payment" value={detail.payment_method} />
      )}
      {detail.payment_charge_id && (
        <Field label="Charge ID" value={detail.payment_charge_id} mono />
      )}
      <Field label="Delivery" value={displayShipping(detail.shipping_method)} />
    </Stack>

    <Stack direction="row" spacing={4} flexWrap="wrap">
      <AddressBlock label="Delivery address" addr={detail.delivery_address} />
      <AddressBlock label="Billing address" addr={detail.customer_address} />
    </Stack>

    {detail.payment_method === 'card' && <PaymentAttemptsPanel order={detail} />}

    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        Line items
      </Typography>
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ borderColor: '#e5e7eb' }}
      >
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#fafafa' }}>
              <TableCell sx={{ fontWeight: 600 }}>Item</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Tenant</TableCell>
              <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Qty</TableCell>
              <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Unit</TableCell>
              <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Line</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {detail.lines.map((l) => (
              <TableRow key={l.id}>
                <TableCell>
                  <Box>{l.snapshot_name}</Box>
                  {l.snapshot_brand && (
                    <Box sx={{ color: '#94a3b8', fontSize: '0.8rem' }}>
                      {l.snapshot_brand}
                    </Box>
                  )}
                </TableCell>
                <TableCell>{l.tenant}</TableCell>
                <TableCell sx={{ textAlign: 'right' }}>{l.qty}</TableCell>
                <TableCell sx={{ textAlign: 'right' }}>{fmtMoney(l.unit_price)}</TableCell>
                <TableCell sx={{ textAlign: 'right' }}>{fmtMoney(l.line_total)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>

    <Stack direction="row" spacing={4} sx={{ justifyContent: 'flex-end' }}>
      <Field label="Subtotal" value={fmtMoney(detail.subtotal)} />
      <Field label="Tax" value={fmtMoney(detail.tax_total)} />
      {detail.shipping_total !== '0' && detail.shipping_total !== '0.00' && (
        <Field label="Shipping" value={fmtMoney(detail.shipping_total)} />
      )}
      {detail.discount_total !== '0' && detail.discount_total !== '0.00' && (
        <Field label="Discount" value={fmtMoney(detail.discount_total)} />
      )}
      <Field label="Total" value={fmtMoney(detail.total)} bold />
    </Stack>
  </Stack>
);

/** What Paysafe holds against this order, on demand.
 *
 *  Nothing about a failed card payment is stored on our side: a decline
 *  leaves the order `pending`, which is the whole of what "card
 *  incomplete" means in the list. Paysafe keeps the auth either way, so
 *  this is where the reason lives — and an empty answer is an answer
 *  too, meaning the customer never got as far as submitting a card.
 *
 *  Not loaded with the order: it's a live call out to Paysafe, and most
 *  orders never need it. */
const PaymentAttemptsPanel = ({ order }: { order: Order }) => {
  const { idToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [result, setResult] = useState<PaymentAttempts | null>(null);

  const check = async () => {
    if (!idToken) return;
    setLoading(true);
    setError('');
    setResult(null);
    try {
      setResult(await getPaymentAttempts(idToken, order.id));
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.status === 502
            ? 'Paysafe could not be reached.'
            : `API error ${err.status}`
          : (err as Error)?.message || 'lookup failed',
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Card payment attempts
        </Typography>
        <Button size="small" onClick={check} disabled={loading || !idToken}>
          {loading ? 'Checking…' : result ? 'Check again' : 'Check Paysafe'}
        </Button>
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}

      {result?.lookup_error && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          Paysafe couldn't be reached, so the charge history below may be
          incomplete. What the customer's browser reported is unaffected.
        </Alert>
      )}

      {result && result.browser_events.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <CheckoutEventList events={result.browser_events} />
        </Box>
      )}

      {result && result.attempts.length === 0 && result.beyond_lookup_window && (
        <Alert severity="warning">
          This order is older than the year Paysafe will search, so the
          lookup can't say either way. Check the merchant portal directly.
        </Alert>
      )}

      {result && result.attempts.length === 0 && !result.beyond_lookup_window && (
        <Alert severity="info">
          No card charge was ever attempted for this order
          {result.checked.length > 0 && ` (checked ${result.checked.join(', ')}`}
          {result.checked.length > 0 &&
            `, ${fmtDate(result.searched_from)} to ${fmtDate(result.searched_to)})`}
          . The checkout never got as far as charging the card — card
          entry and 3-D Secure both happen in the browser, before any
          charge is attempted, so a failure there leaves nothing at
          Paysafe. A decline would appear here as a FAILED row.
          {result.browser_events.length === 0 &&
            ' The browser reported nothing either, so this order predates' +
              ' checkout reporting or the page was closed before it could send.'}
        </Alert>
      )}

      {result && result.attempts.length > 0 && (
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ borderColor: '#e5e7eb' }}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#fafafa' }}>
                <TableCell sx={{ fontWeight: 600 }}>When</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Result</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Card</TableCell>
                <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>
                  Amount
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {result.attempts.map((a) => (
                <AttemptRow key={`${a.environment}:${a.id}`} attempt={a} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

/** Paysafe's status vocabulary, coloured the way the rest of the page
 *  colours state: amber for "needs attention", green for money taken. */
const attemptSx = (status?: string | null) => {
  if (status === 'COMPLETED') return { color: '#15803d', fontWeight: 500 };
  if (status === 'FAILED') return { color: '#b45309', fontWeight: 500 };
  return undefined;
};

const AttemptRow = ({ attempt }: { attempt: PaymentAttempt }) => {
  // AVS/CVV are the usual reason a card "just failed" — a mismatch on
  // either declines a card that has the money — so they go next to
  // Paysafe's own message rather than behind another click.
  const checks = [
    attempt.avsResponse && `AVS ${attempt.avsResponse}`,
    attempt.cvvVerification && `CVV ${attempt.cvvVerification}`,
  ].filter(Boolean) as string[];

  return (
    <TableRow>
      <TableCell>{attempt.txnTime ? fmtDate(attempt.txnTime) : '—'}</TableCell>
      <TableCell sx={attemptSx(attempt.status)}>
        {attempt.status ?? 'unknown'}
        {attempt.environment === 'TEST' && (
          <Box sx={{ color: '#94a3b8', fontSize: '0.75rem' }}>sandbox</Box>
        )}
      </TableCell>
      <TableCell>
        {attempt.card?.lastDigits
          ? `${attempt.card.type ?? 'card'} ••${attempt.card.lastDigits}`
          : '—'}
      </TableCell>
      <TableCell sx={{ textAlign: 'right' }}>
        {typeof attempt.amount === 'number'
          ? `$${(attempt.amount / 100).toFixed(2)}`
          : '—'}
      </TableCell>
      <TableCell>
        {attempt.error?.message && (
          <Box>
            {attempt.error.code ? `${attempt.error.code}: ` : ''}
            {attempt.error.message}
          </Box>
        )}
        {attempt.authCode && (
          <Box sx={{ color: '#94a3b8', fontSize: '0.8rem' }}>
            auth {attempt.authCode}
          </Box>
        )}
        {checks.length > 0 && (
          <Box sx={{ color: '#94a3b8', fontSize: '0.8rem' }}>
            {checks.join(' · ')}
          </Box>
        )}
        <Box sx={{ color: '#94a3b8', fontFamily: 'monospace', fontSize: '0.75rem' }}>
          {attempt.id}
        </Box>
      </TableCell>
    </TableRow>
  );
};

/** How each stage reads to someone trying to work out what happened.
 *  The stage strings are the payment SDK's vocabulary and mean nothing
 *  on their own. */
const STAGE_TEXT: Record<string, string> = {
  opened: 'Payment window opened',
  BeforePayment: 'Closed the payment window before entering a card',
  DuringPayment: 'Closed the payment window mid-payment (3-D Secure runs here)',
  sdk_error: 'The payment window itself failed',
  declined: 'Card was declined',
  payment_failed: 'Payment failed',
};

const CheckoutEventList = ({ events }: { events: CheckoutEvent[] }) => {
  // An 'opened' that is the last word, on an order that was never paid,
  // is the case worth naming out loud: the customer was left sitting in
  // the payment window and nothing ever came back.
  const endedSilently =
    events.length > 0 && events[events.length - 1].stage === 'opened';
  const mobile = events.some((e) => /Mobile|Android|iPhone|iPad/i.test(e.user_agent ?? ''));

  return (
    <Box>
      <Typography variant="caption" sx={{ color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        What the browser reported
      </Typography>
      <Stack spacing={0.5} sx={{ mt: 0.5 }}>
        {events.map((e) => (
          <Box key={e.id} sx={{ fontSize: '0.875rem' }}>
            <Box component="span" sx={{ color: '#94a3b8', mr: 1 }}>
              {fmtDate(e.created_at)}
            </Box>
            {STAGE_TEXT[e.stage] ?? e.stage}
            {e.reason && STAGE_TEXT[e.stage] !== e.reason && (
              <Box sx={{ color: '#94a3b8', fontSize: '0.8rem', pl: 2 }}>{e.reason}</Box>
            )}
          </Box>
        ))}
      </Stack>
      {endedSilently && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          The payment window opened and nothing came back — no close, no
          error. The customer was most likely left waiting and gave up
          {mobile && ', on a phone (a 3-D Secure challenge that switches to a banking app can kill the page)'}
          . This is worth chasing: it is our failure, not an abandoned cart.
        </Alert>
      )}
    </Box>
  );
};

const Field = ({
  label,
  value,
  bold,
  mono,
}: {
  label: string;
  value: string;
  bold?: boolean;
  mono?: boolean;
}) => (
  <Box>
    <Typography
      variant="caption"
      sx={{ color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        fontWeight: bold ? 600 : 400,
        fontFamily: mono ? 'monospace' : undefined,
        fontSize: mono ? '0.8rem' : undefined,
      }}
    >
      {value}
    </Typography>
  </Box>
);

export default OrdersPage;
